import { useRef, useState } from 'react';
import { Button, ButtonGroup, Divider, Menu, MenuItem } from '@mui/material';
import {
  Download as DownloadIcon,
  Print as PrintIcon,
  Autorenew as AutorenewIcon,
  KeyboardArrowDown as ArrowDownIcon,
} from '@mui/icons-material';
import {
  userHasPermission,
  BUSINESS_ROLE_GROUPS,
} from '@/components/security/AppRoles';
import { useUserInfo } from '@/hooks';
import {
  LABEL_PROVIDER_LABELS,
  LabelProvider,
} from '../services/shippings.service';
import { ShippingApi } from '../models';

export interface LabelActionsButtonProps {
  shipping: ShippingApi;
  onPrint: () => void;
  onDownload: (provider: LabelProvider) => void;
  onPrintWithProvider: (provider: LabelProvider) => void;
  onRegenerate?: () => void;
  disabled?: boolean;
}

const PROVIDERS: LabelProvider[] = [
  LabelProvider.SOYDELIVERY,
  LabelProvider.AHIVA,
];

const outlinedSx = {
  borderColor: 'var(--primary-color)',
  '&:hover': { borderColor: '#FF560E' },
  textTransform: 'none' as const,
  whiteSpace: 'nowrap' as const,
};

/**
 * Acción principal de etiqueta (imprimir) con las variantes menos frecuentes
 * plegadas en un desplegable: regenerar, e imprimir o descargar salteando la
 * evaluación automática de reglas para elegir un proveedor puntual. Se usa
 * cuando la regla resuelve mal y hay que reimprimir con la otra etiqueta sin
 * tocar la configuración.
 */
const LabelActionsButton = ({
  onPrint,
  onDownload,
  onPrintWithProvider,
  onRegenerate,
  disabled = false,
}: LabelActionsButtonProps) => {
  const groupRef = useRef<HTMLDivElement>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { permissions } = useUserInfo();

  const canManageProviders = userHasPermission(
    permissions,
    BUSINESS_ROLE_GROUPS.adminOrSupervisor
  );
  const hasMenu = Boolean(onRegenerate) || canManageProviders;
  const open = Boolean(anchorEl);

  const handleClose = () => setAnchorEl(null);

  const run = (action: () => void) => {
    handleClose();
    action();
  };

  const printButton = (
    <Button
      variant='outlined'
      color='secondary'
      onClick={onPrint}
      disabled={disabled}
      startIcon={<PrintIcon fontSize='small' />}
      sx={outlinedSx}
    >
      Imprimir etiqueta
    </Button>
  );

  if (!hasMenu) return printButton;

  return (
    <>
      <ButtonGroup ref={groupRef} variant='outlined' disabled={disabled}>
        {printButton}
        <Button
          variant='outlined'
          color='secondary'
          onClick={() => setAnchorEl(groupRef.current)}
          disabled={disabled}
          aria-label='Más opciones de etiqueta'
          sx={{ ...outlinedSx, px: 0.5, minWidth: 36 }}
        >
          <ArrowDownIcon
            fontSize='small'
            sx={{
              transition: 'transform 0.15s ease',
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          />
        </Button>
      </ButtonGroup>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: { sx: { mt: 1, borderRadius: 2, minWidth: 260 } },
          backdrop: { sx: { cursor: 'pointer' } },
        }}
      >
        {onRegenerate ? (
          <MenuItem
            onClick={() => run(onRegenerate)}
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <AutorenewIcon fontSize='small' />
            Regenerar etiqueta
          </MenuItem>
        ) : null}
        {onRegenerate && canManageProviders ? <Divider /> : null}
        {canManageProviders
          ? PROVIDERS.flatMap((provider, index) => [
              ...(index > 0 ? [<Divider key={`divider-${provider}`} />] : []),
              <MenuItem
                key={`print-${provider}`}
                onClick={() => run(() => onPrintWithProvider(provider))}
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <PrintIcon fontSize='small' />
                {`Imprimir ${LABEL_PROVIDER_LABELS[provider]}`}
              </MenuItem>,
              <MenuItem
                key={`download-${provider}`}
                onClick={() => run(() => onDownload(provider))}
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <DownloadIcon fontSize='small' />
                {`Descargar ${LABEL_PROVIDER_LABELS[provider]}`}
              </MenuItem>,
            ])
          : null}
      </Menu>
    </>
  );
};

export default LabelActionsButton;
